import React from 'react';
import { getPath, readField, setPath } from './caseBuilderModel';

function parseStrings(text) {
  return String(text || '')
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
}

function FieldControl({ field, value, error, onChange }) {
  const id = field.path;
  if (field.type === 'bool') {
    return (
      <label className="cb-check" htmlFor={id}>
        <input
          id={id}
          type="checkbox"
          checked={Boolean(value)}
          disabled={field.readOnly}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span>{field.label}{field.required ? <em> *</em> : null}</span>
      </label>
    );
  }
  if (field.type === 'text') {
    return (
      <label className="cb-field" htmlFor={id}>
        <span>{field.label}{field.required ? <em> *</em> : null}</span>
        <textarea
          id={id}
          rows={3}
          value={value ?? ''}
          readOnly={field.readOnly}
          onChange={(e) => onChange(field.nullable && e.target.value === '' ? null : e.target.value)}
        />
        {error ? <small>{error}</small> : null}
      </label>
    );
  }
  if (field.type === 'strings') {
    const shown = Array.isArray(value) ? value.join(', ') : (value ?? '');
    return (
      <label className="cb-field" htmlFor={id}>
        <span>{field.label}{field.required ? <em> *</em> : null}</span>
        <input
          id={id}
          value={shown}
          placeholder="Comma-separated"
          onChange={(e) => onChange(parseStrings(e.target.value))}
        />
        {error ? <small>{error}</small> : null}
      </label>
    );
  }
  return (
    <label className="cb-field" htmlFor={id}>
      <span>{field.label}{field.required ? <em> *</em> : null}</span>
      <input
        id={id}
        type={field.type === 'number' ? 'number' : 'text'}
        step={field.type === 'number' ? '0.01' : undefined}
        value={value ?? ''}
        readOnly={field.readOnly}
        onChange={(e) => {
          const raw = e.target.value;
          if (field.type === 'number') {
            onChange(raw === '' ? (field.nullable ? null : '') : Number(raw));
            return;
          }
          onChange(field.nullable && raw === '' ? null : raw);
        }}
      />
      {error ? <small>{error}</small> : null}
    </label>
  );
}

function itemBlank(fields) {
  const blank = {};
  fields.forEach((field) => {
    let value = '';
    if (field.type === 'bool') value = false;
    else if (field.type === 'number') value = field.nullable ? null : 0;
    else if (field.type === 'strings') value = [];
    else if (field.nullable) value = null;
    const next = setPath(blank, field.path, value);
    Object.assign(blank, next);
  });
  return blank;
}

export default function BundleForm({ section, document, errors, onChange }) {
  if (!section) return null;

  if (section.arrayPath && section.nestedArray) {
    const rows = getPath(document, section.arrayPath) || [];
    return (
      <div className="cb-section">
        <h2>{section.label}</h2>
        {rows.map((row, rowIndex) => {
          const nested = row?.[section.nestedArray] || [];
          return (
            <article className="cb-card" key={`${row.line_id || rowIndex}`}>
              <header>Line {row.line_number || rowIndex + 1} · {row.line_id || 'line'}</header>
              {nested.map((child, childIndex) => (
                <div className="cb-grid" key={childIndex}>
                  {section.fields.map((field) => {
                    const errKey = `${section.arrayPath}.${rowIndex}.${section.nestedArray}.${childIndex}.${field.path}`;
                    return (
                      <FieldControl
                        key={field.path}
                        field={field}
                        value={readField(child, field)}
                        error={errors[errKey]}
                        onChange={(value) => {
                          const nextChild = setPath(child, field.path, value);
                          const nextNested = nested.slice();
                          nextNested[childIndex] = nextChild;
                          const nextRows = rows.slice();
                          nextRows[rowIndex] = { ...row, [section.nestedArray]: nextNested };
                          onChange(setPath(document, section.arrayPath, nextRows));
                        }}
                      />
                    );
                  })}
                  <button
                    type="button"
                    className="dm-btn dm-btn--ghost"
                    onClick={() => {
                      const nextNested = nested.filter((_, i) => i !== childIndex);
                      const nextRows = rows.slice();
                      nextRows[rowIndex] = { ...row, [section.nestedArray]: nextNested };
                      onChange(setPath(document, section.arrayPath, nextRows));
                    }}
                  >
                    Remove adjustment
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="dm-btn dm-btn--secondary"
                onClick={() => {
                  const nextNested = nested.concat([itemBlank(section.fields)]);
                  const nextRows = rows.slice();
                  nextRows[rowIndex] = { ...row, [section.nestedArray]: nextNested };
                  onChange(setPath(document, section.arrayPath, nextRows));
                }}
              >
                Add adjustment
              </button>
            </article>
          );
        })}
      </div>
    );
  }

  if (section.arrayPath) {
    const rows = getPath(document, section.arrayPath) || [];
    return (
      <div className="cb-section">
        <header className="cb-section__bar">
          <h2>{section.label}</h2>
          <button
            type="button"
            className="dm-btn dm-btn--secondary"
            onClick={() => onChange(setPath(document, section.arrayPath, rows.concat([itemBlank(section.fields)])))}
          >
            Add {section.itemLabel || 'row'}
          </button>
        </header>
        {rows.length === 0 ? <p className="cb-empty">None yet.</p> : null}
        {rows.map((row, rowIndex) => (
          <article className="cb-card" key={rowIndex}>
            <header>
              <span>{section.itemLabel} {rowIndex + 1}</span>
              <button
                type="button"
                className="dm-btn dm-btn--ghost"
                onClick={() => onChange(setPath(document, section.arrayPath, rows.filter((_, i) => i !== rowIndex)))}
              >
                Remove
              </button>
            </header>
            <div className="cb-grid">
              {section.fields.map((field) => {
                const errKey = `${section.arrayPath}.${rowIndex}.${field.path}`;
                return (
                  <FieldControl
                    key={field.path}
                    field={field}
                    value={readField(row, field)}
                    error={errors[errKey]}
                    onChange={(value) => {
                      const nextRows = rows.slice();
                      nextRows[rowIndex] = setPath(row, field.path, value);
                      onChange(setPath(document, section.arrayPath, nextRows));
                    }}
                  />
                );
              })}
            </div>
          </article>
        ))}
      </div>
    );
  }

  return (
    <div className="cb-section">
      <h2>{section.label}</h2>
      <div className="cb-grid">
        {section.fields.map((field) => (
          <FieldControl
            key={field.path}
            field={field}
            value={readField(document, field)}
            error={errors[field.path]}
            onChange={(value) => onChange(setPath(document, field.path, value))}
          />
        ))}
      </div>
    </div>
  );
}
